import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTshirt, faMagic, faSpinner, faArrowLeft, faArrowRight, faPencilAlt, faTrash, faCloudUploadAlt, faLock } from '@fortawesome/free-solid-svg-icons';
import EnhancedImageUpload from './EnhancedImageUpload';

const Step2 = ({ 
    uploadedImages, 
    gridSize, 
    requiredImages, 
    handleImageUploaded,
    handleAdjustImage,
    handleDeleteImage,
    handleGenerateClick,
    loading,
    generateDisabled,
    sessionId,
    isProcessingImages,
    processingError,
    showWarning,
    prevStep,
    nextStep,
    quiltImageUrl,
    currentStep
}) => {
    // Add state to track when generation is complete and redirect is pending
    const [redirectPending, setRedirectPending] = useState(false);
    
    // Add a function to check if all uploaded images are processed
    const allImagesProcessed = () => {
        if (uploadedImages.length < requiredImages) return false;
        return uploadedImages.every(image => !!image.processed);
    };
    
    // Effect to handle redirection after generation completes
    useEffect(() => {
        // Only redirect if we have a quilt image URL and redirect is pending
        if (quiltImageUrl && redirectPending) {
            console.log("Layout generation detected, navigating to Step 3");
            // Small delay to ensure UI updates first
            const redirectTimer = setTimeout(() => {
                nextStep();
                setRedirectPending(false);
            }, 1000);
            
            return () => clearTimeout(redirectTimer);
        }
    }, [quiltImageUrl, redirectPending, nextStep]);

    // Modified generate click handler to mark redirect as pending
    const handleGenerateWithRedirect = async () => {
        try {
            console.log("Starting layout generation with auto-redirect");
            // Set flag to indicate redirect should happen once generation completes
            setRedirectPending(true);
            
            // Call the original handleGenerateClick function
            await handleGenerateClick();
            
            // Note: The redirect will be handled by the useEffect when quiltImageUrl becomes available
        } catch (error) {
            console.error("Error during layout generation:", error);
            setRedirectPending(false);
        }
    };

    return (
        <div className="bg-white rounded-lg p-3 sm:p-6 shadow-md">
            <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#9CAF88]" style={{ fontFamily: "Playfair Display" }}>
                <FontAwesomeIcon icon={faTshirt} className="mr-2" />
                Upload Your Clothing Items
            </h2>
            
            {uploadedImages.length === requiredImages ? (
                <p className="text-green-600 mb-4 sm:mb-6 text-sm sm:text-base font-medium">
                    Your clothing items have been successfully uploaded for your {gridSize} quilt layout.
                </p>
            ) : (
                <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                    Take clear photos of your clothing items spread flat against a contrasting background.
                </p>
            )}
            
            {/* Conditional: Show cutting recommendation OR layout summary - Updated with sage green colors */}
            <div className="bg-[#F0F4ED] p-3 sm:p-4 rounded-lg border-l-4 border-[#9CAF88] mb-4 sm:mb-6">
                {uploadedImages.length === requiredImages ? (
                    <>
                        <h4 className="font-semibold text-[#7A8B73] mb-2">📋 Layout Summary:</h4>
                        <div className="flex flex-wrap gap-2 text-sm">
                            <span className="bg-white px-2 py-1 rounded border border-[#DCE8DB]">
                                <span className="text-[#7A8B73] font-medium">Grid Size:</span> {gridSize}
                            </span>
                            <span className="bg-white px-2 py-1 rounded border border-[#DCE8DB]">
                                <span className="text-[#7A8B73] font-medium">Required Images:</span> {requiredImages}
                            </span>
                        </div>
                    </>
                ) : (
                    <ul className="list-disc pl-5 text-[#7A8B73] space-y-1 text-sm sm:text-base">
                        <li>Please upload exactly {requiredImages} items for your {gridSize} quilt</li>
                        <li>Use good lighting for best color accuracy</li>
                    </ul>
                )}
            </div>
            
            {/* Updated upload controls - cleaner modern UI */}
            <div className="mb-6">
                <div className={`border-2 border-dashed ${uploadedImages.length >= requiredImages ? 'border-gray-300 bg-gray-50' : 'border-[#9CAF88]'} rounded-lg p-6 flex flex-col items-center justify-center text-center min-h-[180px]`}>
                    <div className="mb-2 text-center">
                        <div className="flex items-center justify-center">
                            <div className={`h-16 w-16 rounded-full flex items-center justify-center ${uploadedImages.length >= requiredImages ? 'bg-gray-200' : 'bg-[#9CAF88] bg-opacity-20'}`}>
                                <FontAwesomeIcon 
                                    icon={faCloudUploadAlt} 
                                    className={`text-3xl ${uploadedImages.length >= requiredImages ? 'text-gray-400' : 'text-[#9CAF88]'}`} 
                                />
                            </div>
                        </div>
                        <p className={`mt-3 mb-2 ${uploadedImages.length >= requiredImages ? 'text-gray-500' : 'text-gray-700'}`}>
                            Upload Images ({uploadedImages.length}/{requiredImages})
                        </p>
                        <p className={`text-sm ${uploadedImages.length >= requiredImages ? 'text-gray-400' : 'text-gray-500'}`}>
                            Click to browse or drag and drop files
                        </p>
                    </div>
                    
                    <div className={`mt-4 ${uploadedImages.length >= requiredImages ? 'opacity-50' : ''}`}>
                        <EnhancedImageUpload
                            sessionId={sessionId}
                            onImageUploaded={handleImageUploaded}
                            maxImages={requiredImages}
                            currentCount={uploadedImages.length}
                            disabled={!sessionId || isProcessingImages || uploadedImages.length >= requiredImages}
                            buttonText="Choose Files"
                            buttonClassName={`px-4 py-2 rounded-lg ${uploadedImages.length >= requiredImages ? 'bg-gray-300 text-gray-600' : 'bg-[#9CAF88] hover:bg-[#8A9B79] text-white'}`}
                        />
                    </div>
                </div>
                
                {/* Supported formats info */}
                <div className="mt-2 text-xs text-gray-500 flex justify-between">
                    <span>Supported formats: JPG, PNG, HEIC • Max size: 10MB per image</span>
                    <span className="font-semibold">{uploadedImages.length}/{requiredImages}</span>
                </div>
            </div>
            
            {/* Upload progress - slim progress bar */}
            {uploadedImages.length > 0 && uploadedImages.length < requiredImages && (
                <div className="mb-6">
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div 
                            className="bg-[#9CAF88] h-1.5 rounded-full transition-all duration-500"
                            style={{ width: `${(uploadedImages.length / requiredImages) * 100}%` }}
                        ></div>
                    </div>
                </div>
            )}
            
            {/* Uploaded images */}
            {uploadedImages.length > 0 && (
                <div className="mb-6">
                    <h3 className="font-bold text-base sm:text-lg mb-3 text-gray-700">Uploaded Items</h3>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-4">
                        {uploadedImages.map((image, index) => {
                            // Debug variables for this image
                            const isProcessed = !!image.processed;
                            // FIX: Change the condition to only check for quiltImageUrl, not generateDisabled
                            const shouldShowControls = isProcessed && !quiltImageUrl;
                            
                            return (
                                <div key={index} className="relative group">
                                    <div className="aspect-square border rounded-lg overflow-hidden bg-gray-100">
                                        <img
                                            src={image.previewUrl || image.url}
                                            alt={`Clothing item ${index + 1}`}
                                            className="w-full h-full object-contain"
                                            onError={(e) => {
                                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iMTIiIHk9IjEyIiBmb250LXNpemU9IjEyIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBhbGlnbm1lbnQtYmFzZWxpbmU9Im1pZGRsZSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZpbGw9IiM5OTk5OTkiPj88L3RleHQ+PC9zdmc+';
                                            }}
                                        />
                                        
                                        {!image.processed && !image.isProcessing && (
                                            <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center text-center p-1">
                                                <span className="text-white text-xs">Processing...</span>
                                            </div>
                                        )}
                                        
                                        {image.isProcessing && (
                                            <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                                                <FontAwesomeIcon icon={faSpinner} spin className="text-white text-xl" />
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="absolute top-1 right-1">
                                        <span className="bg-[#9CAF88] bg-opacity-75 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm">
                                            #{index + 1}
                                        </span>
                                    </div>
                                    
                                    {/* Control buttons */}
                                    {image.processed && (
                                        <div 
                                            className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent transition-opacity duration-300 p-2 flex justify-center gap-2 ${
                                                quiltImageUrl ? 'opacity-0 pointer-events-none' : 'opacity-0 group-hover:opacity-100'
                                            }`}
                                            onClick={(e) => {
                                                if (quiltImageUrl) {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }
                                            }}
                                        >
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (!quiltImageUrl) {
                                                        handleAdjustImage(index);
                                                    }
                                                }}
                                                className={`bg-blue-500 text-white w-8 h-8 rounded-full flex items-center justify-center ${
                                                    quiltImageUrl ? 'cursor-not-allowed opacity-50' : 'hover:bg-blue-600'
                                                }`}
                                                title={quiltImageUrl ? "Editing locked - layout is generated" : "Edit image"}
                                                disabled={quiltImageUrl}
                                                aria-label="Edit image"
                                            >
                                                <FontAwesomeIcon icon={faPencilAlt} />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (!quiltImageUrl) {
                                                        handleDeleteImage(index);
                                                    }
                                                }}
                                                className={`bg-red-500 text-white w-8 h-8 rounded-full flex items-center justify-center ${
                                                    quiltImageUrl ? 'cursor-not-allowed opacity-50' : 'hover:bg-red-600'
                                                }`}
                                                title={quiltImageUrl ? "Deleting locked - layout is generated" : "Delete image"}
                                                disabled={quiltImageUrl}
                                                aria-label="Delete image"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    )}
                                    
                                    {/* Add a "locked" indicator for images when layout is generated */}
                                    {quiltImageUrl && image.processed && (
                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-2 flex justify-center">
                                            <span className="text-white text-xs bg-black bg-opacity-50 px-2 py-1 rounded-full">
                                                &#128274; Locked
                                            </span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            
            {/* Generate button - Updated to check for processing status */}
            {uploadedImages.length === requiredImages && (
                <div className="mt-4 sm:mt-6 bg-[#F0F4ED] border-l-4 border-[#9CAF88] p-3 sm:p-4 rounded-lg">
                    <p className="text-[#7A8B73] font-medium text-sm sm:text-base">
                        {quiltImageUrl ? 
                            "Your quilt layout has been generated. View it in the next step." : 
                            redirectPending ? 
                                "Generating your layout and preparing to show results..." :
                            allImagesProcessed() ?
                                "All images uploaded and processed! You're ready to generate your quilt layout." :
                                "All images uploaded! Please wait while we finish processing your images."
                        }
                    </p>
                    
                    <div className="mt-3 sm:mt-4">
                        <button
                            onClick={handleGenerateWithRedirect} 
                            disabled={loading || quiltImageUrl || !allImagesProcessed() || redirectPending}
                            className={`w-full py-2 sm:py-3 rounded-lg flex items-center justify-center font-semibold ${
                                loading || quiltImageUrl || !allImagesProcessed() || redirectPending
                                    ? 'bg-gray-400 cursor-not-allowed text-white'
                                    : 'bg-[#9CAF88] hover:bg-[#8A9B79] text-white'
                            }`}
                        >
                            {loading || redirectPending ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                    <span className="text-sm sm:text-base">
                                        {redirectPending ? "Generating Your Layout..." : "Generating Your Layout..."}
                                    </span>
                                </>
                            ) : quiltImageUrl ? (
                                <>
                                    <FontAwesomeIcon icon={faMagic} className="mr-2" />
                                    <span className="text-sm sm:text-base">Layout Generated</span>
                                </>
                            ) : !allImagesProcessed() ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                    <span className="text-sm sm:text-base">Processing Images...</span>
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faMagic} className="mr-2" />
                                    <span className="text-sm sm:text-base">Generate Basic Layout</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Information message when layout exists and no changes have been made */}
                        {/* Editing locked message - NEW */}
            {quiltImageUrl && (
                <div className="mt-4 sm:mt-6 bg-[#FFF8F2] border border-[#E9DAC8] p-3 sm:p-4 rounded-lg">
                    <div className="flex items-start mb-2">
                        <FontAwesomeIcon icon={faLock} className="text-[#8B6F47] text-lg sm:text-xl mt-0.5 mr-3" />
                        <h4 className="text-[#8B6F47] font-bold text-sm sm:text-base">Editing Locked!</h4>
                    </div>
                    <p className="text-gray-700 text-sm sm:text-base mb-2">
                        Your layout is now locked to maintain consistency. To make any changes (e.g., add, remove, or edit items), please start a new project or reset.
                    </p>
                     <div className="flex flex-col">
                        <p className="text-xs sm:text-sm text-gray-600">
                            This section is now view-only.
                        </p>
                        <p className="text-xs sm:text-sm text-[#8B6F47]">
                            <a 
                                href="#" 
                                onClick={(e) => {
                                    e.preventDefault();
                                    nextStep(); // Navigate to Step 3 when clicked
                                }} 
                                className="text-[#8B6F47] underline"
                            >
                                View this 'quilt layout' instead.
                            </a>
                        </p>
                    </div>
                </div>
            )}
            
            {showWarning && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 sm:p-4 my-4 sm:my-6">
                    <p className="text-yellow-700 text-sm sm:text-base">{showWarning}</p>
                </div>
            )}
            
            {processingError && (
                <div className="bg-red-50 border-l-4 border-red-500 p-3 sm:p-4 my-4 sm:my-6">
                    <p className="text-red-700 text-sm sm:text-base">{processingError}</p>
                </div>
            )}
            
            {/* Navigation buttons */}
            <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 mt-4 sm:mt-6">
                <button
                    onClick={() => {
                        console.log("Back button clicked in Step 2"); // Add debug log
                        prevStep(); // Should directly call the prevStep function passed as a prop
                    }}
                    className="order-2 sm:order-1 w-full sm:w-auto px-4 py-2 bg-[#B8A99A] hover:bg-[#A69688] rounded-lg flex items-center justify-center sm:justify-start"
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back to Dimensions
                </button>
                
                {quiltImageUrl && (
                    <button
                        onClick={nextStep}
                        className="order-1 sm:order-2 w-full sm:w-auto px-4 py-2 bg-[#9CAF88] hover:bg-[#8A9B79] text-white rounded-lg flex items-center justify-center sm:justify-end"
                    >
                        View Layout <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default Step2;